/**
 * 对话框组件
 * 基于 @base-ui/react Dialog 原语实现
 */
import * as React from 'react'
import { Dialog as BaseDialog } from '@base-ui/react/dialog'
import { X, GripHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'

/* ============================================
 * 子组件
 * ============================================ */

export function DialogTrigger({
  children,
  ...props
}: React.ComponentProps<typeof BaseDialog.Trigger>) {
  return <BaseDialog.Trigger {...props}>{children}</BaseDialog.Trigger>
}

export function DialogPortal({
  children,
  ...props
}: React.ComponentProps<typeof BaseDialog.Portal>) {
  return <BaseDialog.Portal {...props}>{children}</BaseDialog.Portal>
}

export function DialogClose({
  children,
  ...props
}: React.ComponentProps<typeof BaseDialog.Close>) {
  return <BaseDialog.Close {...props}>{children}</BaseDialog.Close>
}

export function DialogTitle({
  className,
  children,
  ...props
}: React.ComponentProps<typeof BaseDialog.Title>) {
  return (
    <BaseDialog.Title
      className={cn('text-lg font-semibold text-neutral-900', className)}
      {...props}
    >
      {children}
    </BaseDialog.Title>
  )
}

export function DialogDescription({
  className,
  children,
  ...props
}: React.ComponentProps<typeof BaseDialog.Description>) {
  return (
    <BaseDialog.Description
      className={cn('text-sm text-neutral-500', className)}
      {...props}
    >
      {children}
    </BaseDialog.Description>
  )
}

export function DialogHeader({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex flex-col space-y-1.5', className)} {...props}>
      {children}
    </div>
  )
}

export function DialogFooter({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-row justify-end space-x-2 pt-4', className)}
      {...props}
    >
      {children}
    </div>
  )
}

export function DialogContent({
  className,
  children,
  size = 'md',
  ...props
}: React.ComponentProps<typeof BaseDialog.Popup> & { size?: 'sm' | 'md' | 'lg' | 'xl' }) {
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-4xl',
    xl: 'max-w-5xl',
  }

  return (
    <BaseDialog.Portal>
      <BaseDialog.Backdrop className="fixed inset-0 z-50 bg-black/50" />
      <BaseDialog.Popup
        className={cn(
          // 小屏宽度留 1rem 左右边距，防止内容贴边；sm 起才限制最小宽度（手机竖屏不足 480px）
          'fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2',
          'bg-white rounded-lg shadow-lg',
          'flex flex-col',
          'focus:outline-none',
          'resize overflow-hidden min-w-0 sm:min-w-[480px] max-h-[90vh]',
          sizeClasses[size],
          className
        )}
        {...props}
      >
        <BaseDialog.Close className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100">
          <X className="h-4 w-4" />
          <span className="sr-only">关闭</span>
        </BaseDialog.Close>
        <div className="overflow-y-auto p-6">
          {children}
        </div>
        <div className="absolute bottom-1 right-1 pointer-events-none text-neutral-300">
          <GripHorizontal className="h-4 w-4 rotate-45" />
        </div>
      </BaseDialog.Popup>
    </BaseDialog.Portal>
  )
}

export function DialogBackdrop({
  className,
  ...props
}: React.ComponentProps<typeof BaseDialog.Backdrop>) {
  return (
    <BaseDialog.Backdrop
      className={cn('fixed inset-0 z-50 bg-black/50', className)}
      {...props}
    />
  )
}

/* ============================================
 * 主组件
 * ============================================ */

interface DialogProps extends BaseDialog.Root.Props {
  children: React.ReactNode
}

export function Dialog({ children, ...rest }: DialogProps) {
  return (
    <BaseDialog.Root {...rest}>
      {children}
    </BaseDialog.Root>
  )
}

export default Dialog
